import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useErrorNotification, useSuccessNotification } from './index'
import { FetchProps, Request, Status } from './fetchRefactoredSchema'
import * as _ from 'lodash'
import requestWithRetry from '../requestWithRetry'
import { isAbortError } from '../createRestApi'
import { trackInFlightGetAbort } from '../inFlightGetAbortRegistry'

export default function useFetchRefactored<T, R = T>(props: FetchProps<T, R> = {}): [Request<T>, Status<T>] {
  const {
    errorMessage,
    successMessage,
    emptyValue = null,
    useNewNotification = false,
    disableRetry = false,
    mapper = (data: R) => data as any as T,
  } = props
  const [loading, setLoading] = useState<boolean>(false)
  const [called, setCalled] = useState<boolean>(false)
  const [result, setResult] = useState<T | null>(_.cloneDeep(emptyValue))
  const [error, setError] = useState<Error | null>(null)
  const showError = useErrorNotification()
  const showSuccess = useSuccessNotification(useNewNotification)
  const mountedRef = useRef(true)
  const inFlightGetsRef = useRef(new Map<AbortController, string>())

  useEffect(() => {
    mountedRef.current = true
    const inFlightGets = inFlightGetsRef.current
    return () => {
      mountedRef.current = false
      inFlightGets.forEach((_, controller) => {
        if (!controller.signal.aborted) {
          controller.abort()
        }
      })
      inFlightGets.clear()
    }
  }, [])

  const request: Request<T> = useCallback(
    async requestConfig => {
      const { method = 'get', url, config, onSuccess, onError } = requestConfig
      // Only reads are aborted on unmount: mutations are often fired from a modal that closes right away.
      const ownsAbortController = method === 'get' && !config?.signal
      const controller = ownsAbortController ? new AbortController() : null
      const signal = controller?.signal ?? config?.signal

      let untrackAbort: (() => void) | null = null
      if (controller) {
        inFlightGetsRef.current.set(controller, url)
        untrackAbort = trackInFlightGetAbort(controller)
      }

      const requestOptions = signal ? { ...(config ?? {}), signal } : config ?? {}
      try {
        setCalled(true)
        setLoading(true)
        setError(null)
        const response: R = await requestWithRetry({ method, url, config: requestOptions }, !disableRetry)
        const result = mapper(response)
        if (mountedRef.current) {
          setResult(result)
        }
        if (onSuccess) {
          onSuccess(result)
        }

        const successNotification = successMessage || requestConfig.successMessage
        if (successNotification) {
          showSuccess(successNotification)
        }
        return result
      } catch (error) {
        if (method === 'get' && isAbortError(error)) {
          // Unmount: never settle. In-flight await (e.g. select-all) still gets AbortError when mounted.
          if (!mountedRef.current) {
            return new Promise<T>(() => undefined)
          }
          throw error
        }
        if (mountedRef.current) {
          // @ts-ignore
          setError(error)
        }
        // @ts-ignore
        onError?.(error)

        const errorNotification = errorMessage || requestConfig.errorMessage
        if (errorNotification && !isAbortError(error)) {
          showError(errorNotification, error)
        }
        throw error
      } finally {
        untrackAbort?.()
        if (controller) {
          inFlightGetsRef.current.delete(controller)
        }
        if (mountedRef.current) {
          setLoading(false)
        }
      }
    },
    [successMessage, showSuccess, errorMessage, showError], // eslint-disable-line react-hooks/exhaustive-deps
  )

  // @ts-ignore
  const status: Status<T> = useMemo(() => ({ loading, called, result, error }), [loading, called, result, error])

  return [request, status]
}
